import axios from 'axios';
import moment from 'moment';
import { REFERRALAPIURL } from '../config';

/**
 * submitReferral
 * Centralized referral submission used by the redesigned portal/modal.
 * Mirrors the original ReferralForm behavior: creates the referral, and (for internal
 * receiving companies) optionally creates an inbound task and/or a workflow instance.
 *
 * @param {Object} args
 * @param {number|null} args.companyId        Receiving (internal) company id, or null for standard/manual.
 * @param {string} args.companyName           Receiving company display name.
 * @param {Object|null} args.activeSetting    Active referral setting for the receiving company.
 * @param {string} args.initiatingCompanyName Referring organization / company name.
 * @param {string} args.clientName            Client / patient name.
 * @param {Object|null} args.clientAddressObj Canonical client address object.
 * @param {Object} args.data                  Combined form data (section 2 + recipient-specific form).
 * @returns {Promise<{success: boolean, referralId?: number, error?: string}>}
 */
export async function submitReferral({
  companyId,
  companyName,
  activeSetting,
  initiatingCompanyName,
  clientName,
  clientAddressObj,
  data,
}) {
  const url = `${REFERRALAPIURL}/referral`;
  const initiatorName = (initiatingCompanyName || data.organization || data.yourName || 'External Referrer').trim();
  const subject = [initiatorName, (clientName || data.patientName || '').trim()].filter(Boolean).join(' - ') || 'Referral';

  const payload = {
    initiating_external_name: initiatorName,
    receiving_company: companyId,
    referral_subject: subject,
    referral_date: moment().format('YYYY-MM-DD'),
    referral_status: 'PENDING',
    data,
    referral_coordinator_emp_id: activeSetting?.referral_coordinator_emp_id || undefined,
    referral_email: activeSetting?.referral_coordinator_emp_email || undefined,
    receiving_company_name: companyName || undefined,
    client_name: clientName || (data.patientName || '').trim() || undefined,
  };

  try {
    const resp = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
    if (resp.data.code !== 200) {
      return { success: false, error: resp.data?.message || `Unexpected response (${resp.status})` };
    }
    const referralId = resp.data.referral_id;

    // Inbound task (only when there is no workflow but a coordinator exists)
    try {
      const hasWorkflow = !!(activeSetting && activeSetting.referral_flow_id);
      const hasCoordinator = !!(activeSetting && activeSetting.referral_coordinator_emp_id);
      if (!hasWorkflow && hasCoordinator && companyId) {
        const addressArray = clientAddressObj ? [clientAddressObj] : [];
        await axios.post(
          `${REFERRALAPIURL}/referral/${referralId}/task`,
          {
            employee_id: activeSetting.referral_coordinator_emp_id,
            company_id: companyId,
            customer_name: (clientName || '').trim() || undefined,
            address: addressArray,
            initiating_company_name: initiatorName || undefined,
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
      }
    } catch (taskErr) {
      console.error('Error creating referral task:', taskErr);
    }

    // Workflow instance (when both flow and coordinator are configured)
    try {
      if (activeSetting && activeSetting.referral_flow_id && activeSetting.referral_coordinator_emp_id && companyId) {
        const addressArray = clientAddressObj ? [clientAddressObj] : [];
        await axios.post(`${REFERRALAPIURL}/workflows/instance`, {
          wf_instanceWF_ID: activeSetting.referral_flow_id,
          wf_instanceStatus: 0,
          wf_instanceEmployee: activeSetting.referral_coordinator_emp_id,
          wf_instanceStartDate: moment().format('MM-DD-YYYY'),
          wf_instanceEndDate: moment().add(30, 'days').format('MM-DD-YYYY'),
          wf_instanceCreatedBy: activeSetting.referral_coordinator_emp_id,
          customer_name: (clientName || '').trim() || undefined,
          address: addressArray.length ? addressArray : [],
          company_id: companyId,
        });
      }
    } catch (flowErr) {
      console.error('Error creating referral workflow:', flowErr);
    }

    return { success: true, referralId };
  } catch (err) {
    const serverMessage = err.response?.data?.message;
    const status = err.response ? `Error ${err.response.status}` : 'Network error — check your connection';
    console.error('Error submitting referral:', err.response || err);
    return { success: false, error: serverMessage || status };
  }
}
